/**
 * salesPanels.tsx — Value Points (feature 4), Objection helper (feature 6),
 * and the Close + Book panel (feature 7).
 */
import { useState } from "react";
import type { CallCardState } from "@/types";
import { VALUE_POINTS, productsForInterest, rankValuePoints, type ValuePoint } from "@/live/content/valuePoints";
import { OBJECTIONS, QUICK_ACCESS_IDS, objection } from "@/live/content/objections";
import { CONSULT_PATHS, DECLINE_OPTIONS, DEPOSIT_MOTION, DISCLOSURES, SHOWROOM } from "@/live/content/close";
import { PanelLabel } from "@/live/panels";

/* ── Value Points (label per req A.2; ranking per 4.3; pin per 4.5) ───────── */

function ValueCard({ point, pinned, onPin }: { point: ValuePoint; pinned: boolean; onPin: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-lg border bg-card p-2.5 ${point.status === "pending" ? "border-dashed border-amber-400" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-sm font-semibold">{point.headline}</p>
        <div className="flex shrink-0 items-center gap-1">
          {point.status === "pending" && (
            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">pending</span>
          )}
          <button
            type="button"
            aria-label={pinned ? "Unpin" : "Pin"}
            onClick={() => onPin(point.id)}
            className={`rounded-md px-1.5 py-0.5 text-xs ${pinned ? "bg-primary text-primary-foreground" : "border text-muted-foreground hover:bg-accent"}`}
          >
            📌
          </button>
        </div>
      </div>
      <p className="mt-0.5 text-sm leading-snug">“{point.line}”</p>
      <button type="button" onClick={() => setOpen((v) => !v)} className="mt-1 text-xs font-medium text-primary hover:underline">
        {open ? "less" : "more"}
      </button>
      {open && <p className="mt-1 text-xs text-muted-foreground">{point.detail}</p>}
    </div>
  );
}

export function ValuePointsPanel({
  card,
  pinned,
  onPin,
  expanded,
}: {
  card: CallCardState;
  pinned: string[];
  onPin: (id: string) => void;
  expanded: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const products = productsForInterest(card.serviceInterest.value);
  const pool = VALUE_POINTS.filter((p) => p.products.some((pr) => products.includes(pr)));
  const ranked = rankValuePoints(pool, card.reasons);
  const pinnedCards = ranked.filter((p) => pinned.includes(p.id));
  const rest = ranked.filter((p) => !pinned.includes(p.id));
  const visible = [...pinnedCards, ...rest].slice(0, showAll || expanded ? ranked.length : 3);
  return (
    <section aria-label="Value Points" className="space-y-1.5">
      <div className="flex items-center justify-between">
        <PanelLabel>Value Points</PanelLabel>
        <span className="text-[11px] text-muted-foreground">
          {card.reasons.length > 0 ? "ranked by reasons" : "guide default order"}
        </span>
      </div>
      <div className="space-y-1.5">
        {visible.map((p) => (
          <ValueCard key={p.id} point={p} pinned={pinned.includes(p.id)} onPin={onPin} />
        ))}
      </div>
      {!expanded && ranked.length > 3 && (
        <button type="button" onClick={() => setShowAll((v) => !v)} className="w-full rounded-md border px-2 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent">
          {showAll ? "SHOW TOP 3" : `VIEW ALL (${ranked.length})`}
        </button>
      )}
    </section>
  );
}

/* ── Objection helper (manual quick row always one tap away, req 6.2) ─────── */

export function ObjectionPanel({
  activeId,
  onOpen,
}: {
  activeId: string | null;
  onOpen: (id: string | null) => void;
}) {
  const active = activeId ? objection(activeId) : null;
  const [openStep, setOpenStep] = useState(false);
  return (
    <section aria-label="Objections" className="space-y-1.5">
      <PanelLabel>Objections</PanelLabel>
      <div className="flex flex-wrap gap-1.5">
        {QUICK_ACCESS_IDS.map((id) => {
          const def = objection(id);
          if (!def) return null;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onOpen(id === activeId ? null : id)}
              className={`min-h-[34px] rounded-full border px-3 text-xs font-medium ${id === activeId ? "border-primary bg-accent" : "hover:bg-accent"}`}
            >
              {def.label}
            </button>
          );
        })}
      </div>
      {active && (
        <div className="rounded-lg border-l-4 border-l-amber-400 bg-card p-3">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold">{active.headline}</p>
            <button type="button" onClick={() => onOpen(null)} className="text-xs text-muted-foreground hover:underline">close</button>
          </div>
          <p className="mt-1 text-sm leading-snug">“{active.verbatim}”</p>
          <button type="button" onClick={() => setOpenStep((v) => !v)} className="mt-1.5 text-xs font-medium text-primary hover:underline">
            {openStep ? "hide steps" : "acknowledge → reframe → advance"}
          </button>
          {openStep && (
            <ol className="mt-1.5 space-y-1 text-xs text-muted-foreground">
              {(["acknowledge", "clarify", "educate", "reconnect", "advance"] as const).map((step) => (
                <li key={step}>
                  <span className="font-semibold capitalize text-foreground">{step}:</span> {active.steps[step]}
                </li>
              ))}
            </ol>
          )}
          {active.status === "pending" && (
            <p className="mt-1.5 text-[11px] text-amber-700 dark:text-amber-400">Parts of this script await owner review (TBD register).</p>
          )}
        </div>
      )}
      {!active && OBJECTIONS.length > 0 && (
        <p className="text-[11px] text-muted-foreground">Detected objections open automatically — or tap a chip.</p>
      )}
    </section>
  );
}

/* ── Close + Book (feature 7: deposit motion, disclosures, decline path) ──── */

export function ClosePanel({
  card,
  overThousand,
  onDone,
}: {
  card: CallCardState;
  overThousand: boolean;
  onDone: (stepId: string) => void;
}) {
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const interest = card.serviceInterest.value;
  const isConsultPath = interest === "ppf" || interest === "ceramic_coating";
  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        next.add(id);
        onDone(id);
      }
      return next;
    });
  };

  if (isConsultPath) {
    const path = interest === "ppf" ? CONSULT_PATHS.ppf : CONSULT_PATHS.coating;
    return (
      <section aria-label="Close" className="space-y-1.5">
        <PanelLabel>Close + Book</PanelLabel>
        <div className="rounded-lg border-2 border-emerald-500/40 bg-card p-3">
          <p className="text-sm font-semibold">{path.label}</p>
          <p className="mt-1 text-sm">“{path.line}”</p>
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {path.preFrames.map((f) => (
              <li key={f}>• {f}</li>
            ))}
            <li>• {SHOWROOM.ask}</li>
          </ul>
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Close" className="space-y-1.5">
      <PanelLabel>Close + Book</PanelLabel>
      <ul className="space-y-1 rounded-lg border bg-card p-1.5">
        {DEPOSIT_MOTION.map((step) => (
          <li key={step.id}>
            <button type="button" onClick={() => toggle(step.id)} className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left hover:bg-accent">
              <span
                className={[
                  "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold",
                  checked.has(step.id) ? "border-emerald-500 bg-emerald-500 text-white" : "border-muted-foreground/40 text-transparent",
                ].join(" ")}
              >
                ✓
              </span>
              <span className="min-w-0">
                <span className={`block text-sm ${checked.has(step.id) ? "font-medium text-emerald-900 dark:text-emerald-100" : ""}`}>
                  {step.label}
                  {step.required && !checked.has(step.id) && <span className="ml-1 text-amber-500">•</span>}
                </span>
                <span className="block truncate text-xs text-muted-foreground">“{step.line}”</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
      <div className="rounded-lg border bg-card p-2 text-xs text-muted-foreground">
        <p className="mb-1 font-semibold text-foreground">Disclosures</p>
        {DISCLOSURES.filter((d) => d.id !== "over_1000" || overThousand).map((d) => (
          <p key={d.id} className={d.id === "over_1000" ? "font-medium text-amber-800 dark:text-amber-300" : ""}>
            • {d.line}
          </p>
        ))}
      </div>
      <div className="rounded-lg border border-dashed bg-card p-2">
        <p className="text-xs font-semibold">If they won't book — set a concrete next step (never “just let us know”):</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {DECLINE_OPTIONS.map((o) => (
            <span key={o.id} className="rounded-full border px-2.5 py-1 text-[11px]" title={o.line}>
              {o.label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
